import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, ImageDown, XCircle, CheckCircle2, MinusCircle, EyeOff, Eye } from 'lucide-react';
import { getGPS, getMatchDrills, getWellnessExclusions, addWellnessExclusion, removeWellnessExclusion } from '../api';
import { parseTimeToMinutes } from '../pages/gps/matchChronicShared';

// ── Date helpers (same shape as WellnessStaff.js's own — kept local, they're one-liners) ──────
const today = () => new Date().toISOString().split('T')[0];
function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().split('T')[0];
}
function dateRange(from, to) {
  const dates = [];
  let d = new Date(from);
  const end = new Date(to);
  while (d <= end) { dates.push(d.toISOString().split('T')[0]); d.setDate(d.getDate() + 1); }
  return dates;
}
function fmtLong(dateStr) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });
}

const PRESETS = [['0', 'Today'], ['1', 'Last 3 days'], ['2', 'Last 7 days'], ['custom', 'Custom']];
const PRESET_DAYS = { '0': 0, '1': 2, '2': 6 };

const FORMS = [
  { key: 'wellness', label: 'Wellness' },
  { key: 'pitch-rpe', label: 'Pitch RPE' },
];

// Who's missing which form on which day, for the squad's active players — Wellness Overview's
// button-triggered popup (explicit request, 2026-09-14). Wellness and RPE are both already
// fetched whole (no date filter) by WellnessStaff's parent load(), so this reuses that data
// rather than firing its own requests — same client-side-filter pattern IndividualView already
// uses for its own date range. GPS sessions are fetched here instead (2026-09-14 follow-up):
// they're a full-season payload nobody else on this page needs, so loading them only when this
// popup actually opens avoids taxing every Wellness Overview visit for a check staff may never run.
export default function MissingSubmissionsModal({ players, wellness, rpeData, onClose }) {
  const [preset, setPreset] = useState('2');
  const [customFrom, setCustomFrom] = useState(addDays(today(), -6));
  const [customTo, setCustomTo] = useState(today());
  const [exporting, setExporting] = useState(false);
  const [gpsSessions, setGpsSessions] = useState(null); // null = still loading
  const [matchDrills, setMatchDrills] = useState([]);
  const [exclusions, setExclusions] = useState([]);
  const [openHiddenDates, setOpenHiddenDates] = useState(new Set());
  const captureRef = useRef(null);

  const loadExclusions = () => getWellnessExclusions().then(setExclusions).catch(err => console.error('Failed to load wellness exclusions:', err));

  useEffect(() => {
    getGPS().then(setGpsSessions).catch(err => { console.error('Failed to load GPS sessions:', err); setGpsSessions([]); });
    // Pre-filtered server-side to session_type='match' + the canonical "match-first/second
    // half-full" drill_title pattern (see server.staging.js's GET /api/drills/match) — the same
    // source Match Performance's and pillarScores.js's own match-minutes numbers come from, so
    // "did they actually play" here agrees with what's shown everywhere else in the app.
    getMatchDrills().then(setMatchDrills).catch(err => { console.error('Failed to load match drills:', err); setMatchDrills([]); });
    loadExclusions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rangeFrom = preset === 'custom' ? customFrom : addDays(today(), -PRESET_DAYS[preset]);
  const rangeTo = preset === 'custom' ? customTo : today();
  const dates = useMemo(() => dateRange(rangeFrom, rangeTo).sort((a, b) => b.localeCompare(a)), [rangeFrom, rangeTo]);

  const activePlayers = useMemo(
    () => [...players].filter(p => p.active !== false).sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [players]
  );

  // One lookup per form: date -> Set of player ids who submitted that day.
  const wellnessByDate = useMemo(() => {
    const m = {};
    // Oura-created rows (submitted_at NULL) hold sleep data, not a submission.
    wellness.filter(e => e.submittedAt).forEach(e => { (m[e.date] ??= new Set()).add(String(e.playerId)); });
    return m;
  }, [wellness]);
  const pitchRpeByDate = useMemo(() => {
    const m = {};
    rpeData.filter(r => r.session_type === 'pitch').forEach(r => {
      const d = (r.date || '').slice(0, 10);
      (m[d] ??= new Set()).add(String(r.player_id));
    });
    return m;
  }, [rpeData]);

  // Minutes played per (session, player) — summed across that match's "full half" drill rows
  // (a sub who only got e.g. a second-half row still sums correctly; a bench player with no row
  // at all totals 0).
  const matchMinutesBySession = useMemo(() => {
    const m = {};
    matchDrills.forEach(d => {
      if (d.player_id == null) return;
      const bySession = (m[d.session_id] ??= {});
      const key = String(d.player_id);
      bySession[key] = (bySession[key] || 0) + parseTimeToMinutes(d.total_time);
    });
    return m;
  }, [matchDrills]);

  // Pitch RPE is only expected from players who were actually out there that day. For a training
  // session, that's "has a gps_data row" — real or estimated both count (explicit request: "or
  // estimated gps data"). For a match session it's stricter (explicit follow-up request): a row
  // in gps_data alone isn't enough — an unused substitute can still get tracked/logged for a
  // match they never entered, so a match player only counts if their first/second-half-full
  // minutes sum to more than 0. A training session elsewhere the same day (e.g. non-playing
  // squad doing separate conditioning) still adds that player normally — this only tightens the
  // match session's own contribution, it doesn't touch the training-day rule above. Wellness has
  // no such gate at all — it's a daily check-in regardless of whether anyone trained or played.
  const gpsPlayersByDate = useMemo(() => {
    const m = {};
    (gpsSessions || []).forEach(s => {
      const d = (s.session_date || '').slice(0, 10);
      if (!d) return;
      const isMatch = s.session_type === 'match';
      const minutesForSession = matchMinutesBySession[s.id] || {};
      (s.players || []).forEach(row => {
        if (row.player_id == null) return;
        const key = String(row.player_id);
        if (isMatch && !(minutesForSession[key] > 0)) return; // matchday, didn't actually play
        (m[d] ??= new Set()).add(key);
      });
    });
    return m;
  }, [gpsSessions, matchMinutesBySession]);

  // Manual per-player/day/form hide (explicit request, 2026-09-14: "sometimes other things
  // happen"; follow-up same day: hiding a player from one form must NOT also hide them from the
  // other — they're independent). Orthogonal to the GPS/match-minutes gating above, for reasons
  // no data source captures — national team duty, a funeral, etc. `ex.date` comes back from
  // Postgres as a full timestamp once JSON-serialized (same trap `.slice(0,10)` is used against
  // everywhere else in this app, e.g. session_date) — normalizing here is what actually makes
  // the hide button take visible effect; without it this Set lookup below silently never matches.
  const exclusionsByDate = useMemo(() => {
    const m = {};
    exclusions.forEach(ex => { const d = (ex.date || '').slice(0, 10); (m[d] ??= []).push(ex); });
    return m;
  }, [exclusions]);

  const hiddenSetFor = (formKey, date) => new Set(
    (exclusionsByDate[date] || []).filter(ex => ex.form === formKey).map(ex => String(ex.player_id))
  );

  const poolFor = (formKey, date) => {
    const hidden = hiddenSetFor(formKey, date);
    const base = formKey === 'pitch-rpe'
      ? activePlayers.filter(p => (gpsPlayersByDate[date] || new Set()).has(String(p.id)))
      : activePlayers;
    return base.filter(p => !hidden.has(String(p.id)));
  };

  const missingFor = (formKey, date) => {
    const submitted = (formKey === 'wellness' ? wellnessByDate : pitchRpeByDate)[date] || new Set();
    return poolFor(formKey, date).filter(p => !submitted.has(String(p.id)));
  };

  const [hiding, setHiding] = useState(null); // "playerId-date-form" currently saving, prevents double-clicks
  const handleHide = async (playerId, date, formKey) => {
    const key = `${playerId}-${date}-${formKey}`;
    setHiding(key);
    try {
      await addWellnessExclusion(playerId, date, formKey);
      await loadExclusions();
    } catch (err) {
      console.error('Failed to hide player:', err);
    } finally {
      setHiding(null);
    }
  };
  const handleUnhide = async (exclusionId) => {
    try {
      await removeWellnessExclusion(exclusionId);
      await loadExclusions();
    } catch (err) {
      console.error('Failed to unhide player:', err);
    }
  };
  const handleReasonBlur = async (exclusion, reason) => {
    if ((exclusion.reason || '') === reason) return;
    try {
      await addWellnessExclusion(exclusion.player_id, (exclusion.date || '').slice(0, 10), exclusion.form, reason);
      await loadExclusions();
    } catch (err) {
      console.error('Failed to save exclusion reason:', err);
    }
  };
  const toggleHiddenOpen = (date) => setOpenHiddenDates(prev => {
    const next = new Set(prev);
    next.has(date) ? next.delete(date) : next.add(date);
    return next;
  });

  const handleExport = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const { default: html2canvas } = await import('html2canvas');
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--bg-card').trim() || '#111827';
      const canvas = await html2canvas(captureRef.current, { scale: 2, useCORS: true, backgroundColor: bgColor, logging: false });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      const label = rangeFrom === rangeTo ? rangeFrom : `${rangeFrom}_to_${rangeTo}`;
      a.download = `missing-submissions-${label}.png`;
      a.click();
    } catch (err) {
      console.error('Missing-submissions PNG export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const rangeLabel = rangeFrom === rangeTo ? fmtLong(rangeFrom) : `${fmtLong(rangeFrom)} – ${fmtLong(rangeTo)}`;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: 920, maxHeight: '88vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 40px 80px rgba(0,0,0,0.6)' }}>

        {/* ── Header ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          <div>
            <h3 style={{ color: 'var(--text-primary)', fontSize: 16, fontWeight: 700, margin: 0 }}>Missing Submissions</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '3px 0 0' }}>Wellness & Pitch RPE, by day, for active players</p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 4, borderRadius: 6 }}><X size={18} /></button>
        </div>

        {/* ── Controls ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 24px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {PRESETS.map(([v, label]) => (
              <button key={v} onClick={() => setPreset(v)}
                style={{ padding: '5px 10px', borderRadius: 6, fontSize: 12, fontFamily: 'inherit', fontWeight: preset === v ? 600 : 400, border: preset === v ? '1px solid #3b82f6' : '1px solid var(--border)', background: preset === v ? '#3b82f6' : 'var(--bg-card)', color: preset === v ? '#fff' : 'var(--text-secondary)', cursor: 'pointer' }}>
                {label}
              </button>
            ))}
            {preset === 'custom' && (
              <>
                <input type="date" value={customFrom} max={customTo} onChange={e => setCustomFrom(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>–</span>
                <input type="date" value={customTo} min={customFrom} max={today()} onChange={e => setCustomTo(e.target.value)}
                  style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
              </>
            )}
          </div>
          <button onClick={handleExport} disabled={exporting}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: exporting ? 'default' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', opacity: exporting ? 0.6 : 1 }}
            title="Export this list as a PNG image to share">
            <ImageDown size={14} /> {exporting ? 'Exporting…' : 'Download image'}
          </button>
        </div>

        {/* ── Scrollable, screenshot-captured content ── */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          <div ref={captureRef} style={{ background: 'var(--bg-card)', padding: '18px 24px 24px' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{rangeLabel}</div>
            <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 16 }}>{activePlayers.length} active players</div>

            {!activePlayers.length ? (
              <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--text-dimmed)' }}>No active players found.</div>
            ) : gpsSessions === null ? (
              <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--text-dimmed)' }}>Loading…</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {dates.map(date => {
                  const hiddenToday = exclusionsByDate[date] || [];
                  const hiddenOpen = openHiddenDates.has(date);
                  return (
                  <div key={date} style={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 12, padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{fmtLong(date)}</div>
                      {hiddenToday.length > 0 && (
                        exporting ? (
                          <span style={{ fontSize: 11, color: 'var(--text-dimmed)' }}>{hiddenToday.length} hidden</span>
                        ) : (
                          <button onClick={() => toggleHiddenOpen(date)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: 'var(--text-dimmed)', fontSize: 11, cursor: 'pointer', padding: 0, fontFamily: 'inherit' }}>
                            <Eye size={11} /> {hiddenToday.length} hidden
                          </button>
                        )
                      )}
                    </div>
                    {hiddenOpen && !exporting && hiddenToday.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10, padding: '8px 10px', background: 'var(--bg-app)', borderRadius: 8 }}>
                        {hiddenToday.map(ex => {
                          const p = activePlayers.find(pl => String(pl.id) === String(ex.player_id));
                          const formLabel = FORMS.find(f => f.key === ex.form)?.label || ex.form;
                          return (
                            <div key={ex.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontSize: 12, color: 'var(--text-secondary)', minWidth: 150 }}>{p?.name || `Player ${ex.player_id}`} <span style={{ color: 'var(--text-dimmed)' }}>· {formLabel}</span></span>
                              <input defaultValue={ex.reason || ''} placeholder="reason (optional)"
                                onBlur={e => handleReasonBlur(ex, e.target.value)}
                                style={{ flex: 1, fontSize: 11, padding: '3px 7px', borderRadius: 5, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', outline: 'none' }} />
                              <button onClick={() => handleUnhide(ex.id)} title="Unhide"
                                style={{ display: 'flex', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}>
                                <EyeOff size={13} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      {FORMS.map(f => {
                        const pool = poolFor(f.key, date);
                        // Pitch RPE on a day nobody has gps_data for (no pitch session at all,
                        // or a rest day) isn't "all submitted" — nobody was expected to submit,
                        // which reads very differently in a screenshot, so it gets its own state.
                        const noOneExpected = f.key === 'pitch-rpe' && pool.length === 0;
                        const missing = noOneExpected ? [] : missingFor(f.key, date);
                        return (
                          <div key={f.key}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                              {noOneExpected
                                ? <MinusCircle size={13} color="var(--text-dimmed)" />
                                : missing.length === 0
                                ? <CheckCircle2 size={13} color="#10b981" />
                                : <XCircle size={13} color="#ef4444" />}
                              {f.label} ·{' '}
                              {noOneExpected ? 'no GPS session'
                                : missing.length === 0 ? 'all submitted'
                                : f.key === 'pitch-rpe' ? `${missing.length}/${pool.length} missing`
                                : `${missing.length} missing`}
                            </div>
                            {missing.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {missing.map(p => {
                                  const key = `${p.id}-${date}-${f.key}`;
                                  return (
                                    <span key={p.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#f87171', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.28)', borderRadius: 6, padding: '3px 6px 3px 8px' }}>
                                      {p.squadNumber ? `#${p.squadNumber} ` : ''}{p.name}
                                      {!exporting && (
                                        <button onClick={() => handleHide(p.id, date, f.key)} disabled={hiding === key} title={`Hide this player from ${f.label} for this day`}
                                          style={{ display: 'flex', background: 'none', border: 'none', color: '#f87171', opacity: 0.7, cursor: hiding === key ? 'default' : 'pointer', padding: 0 }}>
                                          <EyeOff size={11} />
                                        </button>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
